import type { Translations } from '@/context/LanguageContext';

type TaxonomyKey =
  | 'groupCategories'
  | 'providerFilters'
  | 'providerSpecialties'
  | 'resourceTopics'
  | 'resourcePopulations'
  | 'resourceCategories'
  | 'blogCategories'
  | 'eventCategories';

// Defensive against test mocks that don't include `t.taxonomy` — every
// helper falls back to the supplied English label when the bucket or key
// is missing, instead of throwing.
const lookup = (
  t: Translations,
  bucket: TaxonomyKey,
  value: string,
): string | undefined => {
  const taxonomy = (t as Partial<Translations>).taxonomy;
  const map = taxonomy?.[bucket] as Record<string, string> | undefined;
  return map?.[value];
};

const ALL = (t: Translations, englishFallback: string) =>
  (t as Partial<Translations>).taxonomy?.common?.all ?? englishFallback;

export const getGroupCategoryLabelI18n = (
  t: Translations,
  value: string | null | undefined,
  englishFallback: string,
): string => {
  if (!value) return '';
  if (value === 'all') return ALL(t, englishFallback);
  return lookup(t, 'groupCategories', value) ?? englishFallback;
};

export const getProviderFilterLabelI18n = (
  t: Translations,
  value: string,
  englishFallback: string,
): string => {
  if (value === 'all') return ALL(t, englishFallback);
  return lookup(t, 'providerFilters', value) ?? englishFallback;
};

export const getProviderSpecialtyLabelI18n = (
  t: Translations,
  value: string,
): string => lookup(t, 'providerSpecialties', value) ?? value;

export const getResourceTopicLabelI18n = (
  t: Translations,
  value: string,
  englishFallback: string,
): string => {
  if (value === 'all') return ALL(t, englishFallback);
  return lookup(t, 'resourceTopics', value) ?? englishFallback;
};

export const getResourcePopulationLabelI18n = (
  t: Translations,
  value: string,
  englishFallback: string,
): string => {
  if (value === 'all') return ALL(t, englishFallback);
  return lookup(t, 'resourcePopulations', value) ?? englishFallback;
};

export const getResourceCategoryLabelI18n = (
  t: Translations,
  value: string,
  englishFallback: string,
): string => lookup(t, 'resourceCategories', value) ?? englishFallback;

export const getBlogCategoryLabelI18n = (
  t: Translations,
  slug: string | null | undefined,
  englishFallback: string,
): string => {
  if (!slug) return englishFallback;
  return lookup(t, 'blogCategories', slug) ?? englishFallback;
};

export const getEventCategoryLabelI18n = (
  t: Translations,
  slug: string | null | undefined,
  englishFallback: string,
): string => {
  if (!slug) return englishFallback;
  return lookup(t, 'eventCategories', slug) ?? englishFallback;
};

// Locale-aware alphabetical sort by the *displayed* label, not the
// underlying (always-English) Strapi `name`/slug — a category translated to
// Spanish sorts by its Spanish label, not by where its English name would
// have landed. Language must be passed explicitly since these are plain
// functions, not hooks (`localeCompare`'s second arg picks locale-correct
// collation, e.g. accented characters sort with their unaccented neighbors).
export const sortByLabel = <T>(
  items: readonly T[],
  getLabel: (item: T) => string,
  language: string,
): T[] => [...items].sort((a, b) => getLabel(a).localeCompare(getLabel(b), language));

// Same as sortByLabel, but for FILTER option lists that include an "All" /
// "Todos" sentinel (identified by `value === 'all'`) — that entry is always
// pinned first, never swept into the alphabetical sort, regardless of what
// letter "All"/"Todos" starts with in the current language.
export const sortFilterOptionsByLabel = <T extends { value: string }>(
  items: readonly T[],
  getLabel: (item: T) => string,
  language: string,
): T[] => {
  const allOption = items.find((item) => item.value === 'all');
  const rest = items.filter((item) => item.value !== 'all');
  const sortedRest = sortByLabel(rest, getLabel, language);
  return allOption ? [allOption, ...sortedRest] : sortedRest;
};
